/* Cart note persistence and mobile checkout. No third-party dependency. */
(() => {
  if (!customElements.get("yatskia-cart-note")) {
    class CartNote extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;

        this.ready = true;
        this.input = this.querySelector("textarea");
        this.status = this.querySelector('[role="status"]');
        this.retry = this.querySelector("[data-note-retry]");
        this.saved = this.input.value;

        this.input.addEventListener("input", () => {
          this.dirty = this.input.value !== this.saved || this.uncertain;

          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.save(), 600);

          this.setStatus(this.dirty ? "Note not saved yet." : "");
          window.YatskiaCartGuard?.sync();
        });

        this.retry.addEventListener("click", () => this.save());
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
      }

      setStatus(text) {
        if (this.status.textContent !== text) this.status.textContent = text;
      }

      async save() {
        if (this.saving || !this.dirty) return;

        if (
          window.YatskiaCartGuard?.quantityBusy() ||
          window.YatskiaCartGuard?.activeEditor()?.saving
        ) {
          this.timer = setTimeout(() => this.save(), 600);
          return;
        }

        this.saving = true;
        this.retry.hidden = true;

        const value = this.input.value;

        this.setStatus("Saving note…");
        window.YatskiaCartGuard?.sync();

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);

        let success = false;

        try {
          const root = window.Shopify?.routes?.root || "/";

          const response = await fetch(`${root}cart/update.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ note: value }),
            signal: controller.signal,
          });

          if (!response.ok) throw new Error("Note could not be saved.");

          const cart = await response.json();

          if ((cart.note || "") !== value)
            throw new Error("Note save could not be confirmed.");

          this.saved = value;
          this.uncertain = false;
          this.dirty = this.input.value !== value;

          this.setStatus(this.dirty ? "Saving latest note…" : "Note saved.");

          success = true;
        } catch {
          this.dirty = true;
          this.uncertain = true;
          this.retry.hidden = false;

          this.setStatus(
            "Your note could not be saved. Select Retry saving note before checkout.",
          );
        } finally {
          clearTimeout(timeout);
          this.saving = false;

          window.YatskiaCartGuard?.sync();

          if (success && this.dirty) this.save();
        }
      }
    }

    customElements.define("yatskia-cart-note", CartNote);
  }

  if (customElements.get("yatskia-cart-checkout")) return;

  class CartCheckout extends HTMLElement {
    connectedCallback() {
      this.cleanup?.abort();
      this.cleanup = new AbortController();

      const opts = { signal: this.cleanup.signal };

      this.mobile = window.matchMedia("(max-width: 749px)");

      this.update = () => {
        const inline = document.getElementById("checkout");
        const button = this.querySelector('button[name="checkout"]');
        const rect = inline?.getBoundingClientRect();

        const visible =
          rect && rect.top >= 0 && rect.bottom <= window.innerHeight;

        const typing = !!document.activeElement?.matches(
          "input,textarea,select",
        );

        this.hidden =
          this.dataset.empty === "true" ||
          !this.mobile.matches ||
          !!visible ||
          !inline ||
          typing;

        if (button) {
          button.disabled = this.dataset.empty === "true" || !!inline?.disabled;

          button.setAttribute(
            "aria-disabled",
            String(button.disabled || !!window.YatskiaCartGuard?.blocked()),
          );
        }
      };

      this.mobile.addEventListener("change", this.update, opts);

      document.addEventListener("yatskia:cart-state", this.update, opts);
      document.addEventListener("focusin", this.update, opts);

      document.addEventListener(
        "focusout",
        () => queueMicrotask(this.update),
        opts,
      );

      window.addEventListener("resize", this.update, opts);

      window.addEventListener("scroll", this.update, {
        ...opts,
        passive: true,
      });

      this.intersection = new IntersectionObserver(this.update, {
        threshold: [0, 1],
      });

      const inline = document.getElementById("checkout");

      if (inline) this.intersection.observe(inline);

      this.update();
    }

    disconnectedCallback() {
      this.cleanup?.abort();
      this.intersection?.disconnect();
    }
  }

  customElements.define("yatskia-cart-checkout", CartCheckout);
})();